import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../components/AppIcon';
import ProjectModal from './components/ProjectModal';
import ProjectCard from './components/ProjectCard';
import FilterBar from './components/FilterBar';
import ClientTestimonials from './components/ClientTestimonials';
import Footer from 'pages/home/components/Footer';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [isLoading, setIsLoading] = useState(true);
  const [visibleProjects, setVisibleProjects] = useState(9);

  const categories = [
    { id: 'All', label: 'All Projects', count: 24 },
    { id: 'Web Development', label: 'Web Development', count: 8 },
    { id: 'Mobile Apps', label: 'Mobile Apps', count: 6 },
    { id: 'Enterprise Solutions', label: 'Enterprise Solutions', count: 5 },
    { id: 'E-commerce', label: 'E-commerce', count: 5 }
  ];

  const projects = [
    {
      id: 1,
      title: "FinTech Dashboard Platform",
      client: "SecureBank Corp",
      category: "Web Development",
      industry: "Financial Services",
      technologies: ["React", "Node.js", "PostgreSQL", "AWS"],
      thumbnail: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=400&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop"
      ],
      description: "A comprehensive financial dashboard providing real-time analytics and transaction monitoring for enterprise banking operations.",
      objectives: `The primary objective was to create a unified dashboard that would consolidate multiple banking systems into a single, intuitive interface. The platform needed to handle real-time data processing, provide advanced analytics, and maintain the highest security standards required in the financial sector.

Key goals included reducing operational overhead by 40%, improving data accessibility for decision-makers, and ensuring compliance with financial regulations while maintaining optimal performance under high transaction volumes.`,
      challenges: `The main technical challenges included integrating with legacy banking systems that used different data formats and protocols. We also needed to implement real-time data synchronization across multiple databases while ensuring zero downtime during peak trading hours.

Security was paramount, requiring implementation of multi-factor authentication, end-to-end encryption, and comprehensive audit trails. Performance optimization was critical as the system needed to process thousands of transactions per second without latency issues.`,
      solutions: `We implemented a microservices architecture using Node.js and Docker containers, allowing for scalable and maintainable code. Real-time data synchronization was achieved through Apache Kafka message queues and Redis caching layers.

For security, we integrated OAuth 2.0 with JWT tokens, implemented role-based access control, and used AES-256 encryption for sensitive data. The frontend was built with React and Redux for state management, providing a responsive and intuitive user experience.`,
      results: `The platform successfully reduced operational costs by 45% and improved data processing speed by 60%. User satisfaction scores increased from 3.2 to 4.7 out of 5, and the system now handles over 50,000 daily transactions with 99.9% uptime.

The client reported significant improvements in decision-making speed and regulatory compliance, with audit preparation time reduced from weeks to days.`,
      completionDate: "2024-01-15",
      projectSize: "Large",
      testimonial: {
        text: "Zentirx delivered beyond our expectations. The dashboard has transformed how we handle financial operations.",
        author: "Sarah Johnson",
        position: "CTO, SecureBank Corp"
      },
      downloadUrl: "/assets/case-studies/fintech-dashboard.pdf"
    },
    {
      id: 2,
      title: "E-Learning Mobile App",
      client: "EduTech Solutions",
      category: "Mobile Apps",
      industry: "Education",
      technologies: ["React Native", "Firebase", "Node.js", "MongoDB"],
      thumbnail: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&h=600&fit=crop"
      ],
      description: "An interactive mobile learning platform with gamification features, progress tracking, and offline content access.",
      objectives: `Create an engaging mobile learning experience that would increase student retention rates and provide flexible learning options. The app needed to support multiple learning styles through interactive content, video lessons, and gamified assessments.

The goal was to achieve 80% course completion rates and provide seamless offline learning capabilities for students in areas with limited internet connectivity.`,
      challenges: `The primary challenge was creating an engaging user experience that would keep students motivated throughout their learning journey. We needed to implement complex gamification mechanics while maintaining educational effectiveness.

Technical challenges included optimizing video content for mobile devices, implementing robust offline synchronization, and creating a scalable backend that could handle thousands of concurrent users during peak learning hours.`,
      solutions: `We developed a React Native application with a comprehensive gamification system including points, badges, leaderboards, and achievement unlocks. The offline functionality was implemented using SQLite for local storage and background synchronization.

Video optimization was achieved through adaptive bitrate streaming and intelligent caching. The backend used Node.js with MongoDB for flexible data storage and real-time progress tracking.`,
      results: `The app achieved a 92% course completion rate, exceeding the target by 12%. Student engagement increased by 150%, and the app now serves over 25,000 active learners across 15 countries.

The offline functionality proved crucial, with 40% of users regularly accessing content without internet connectivity. App store ratings average 4.8/5 stars.`,
      completionDate: "2023-11-20",
      projectSize: "Medium",
      testimonial: {
        text: "The app has revolutionized how our students learn. Engagement rates have never been higher.",
        author: "Dr. Michael Chen",
        position: "Head of Digital Learning, EduTech Solutions"
      },
      downloadUrl: "/assets/case-studies/elearning-app.pdf"
    },
    {
      id: 3,
      title: "Supply Chain Management System",
      client: "GlobalLogistics Inc",
      category: "Enterprise Solutions",
      industry: "Logistics",
      technologies: ["Angular", "Java Spring", "Oracle", "Docker"],
      thumbnail: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=600&h=400&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1494412651409-8963ce7935a7?w=800&h=600&fit=crop"
      ],
      description: "Enterprise-grade supply chain management platform with real-time tracking, inventory optimization, and predictive analytics.",
      objectives: `Develop a comprehensive supply chain management system that would provide end-to-end visibility across global operations. The system needed to integrate with existing ERP systems and provide real-time tracking of shipments, inventory levels, and supplier performance.

Key objectives included reducing inventory costs by 25%, improving delivery accuracy to 99.5%, and providing predictive analytics for demand forecasting.`,
      challenges: `The main challenge was integrating with multiple legacy systems across different geographical locations, each with unique data formats and business processes. We also needed to implement real-time tracking for thousands of shipments simultaneously.

Performance was critical as the system needed to process millions of data points daily while providing instant access to critical information for decision-makers across different time zones.`,
      solutions: `We implemented a microservices architecture using Java Spring Boot and Docker containers for scalability. Integration with legacy systems was achieved through custom APIs and message queues for reliable data synchronization.

Real-time tracking was implemented using IoT sensors and GPS data, processed through Apache Kafka streams. The frontend Angular application provided role-based dashboards with real-time updates and advanced filtering capabilities.`,
      results: `The system reduced inventory holding costs by 30% and improved on-time delivery rates to 99.7%. Processing time for supply chain decisions decreased from hours to minutes, and the platform now manages over $500M in annual logistics operations.

Predictive analytics helped prevent 95% of potential stockouts, significantly improving customer satisfaction and operational efficiency.`,
      completionDate: "2023-09-10",
      projectSize: "Large",
      testimonial: {
        text: "This system has transformed our global operations. We now have unprecedented visibility and control.",
        author: "Robert Martinez",
        position: "VP Operations, GlobalLogistics Inc"
      },
      downloadUrl: "/assets/case-studies/supply-chain-system.pdf"
    },
    {
      id: 4,
      title: "Fashion E-commerce Platform",
      client: "StyleHub Fashion",
      category: "E-commerce",
      industry: "Retail",
      technologies: ["Next.js", "Stripe", "PostgreSQL", "Redis"],
      thumbnail: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=600&h=400&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1472851294608-062f824d29cc?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop"
      ],
      description: "Modern e-commerce platform with AI-powered recommendations, virtual try-on features, and seamless checkout experience.",
      objectives: `Create a cutting-edge e-commerce platform that would differentiate StyleHub in the competitive fashion market. The platform needed to provide personalized shopping experiences, reduce cart abandonment rates, and increase average order values.

Goals included achieving a conversion rate of 3.5%, reducing page load times to under 2 seconds, and implementing innovative features like virtual try-on and AI-powered styling recommendations.`,
      challenges: `The main challenge was implementing AI-powered recommendation algorithms that would provide accurate and relevant product suggestions. We also needed to integrate virtual try-on technology while maintaining fast page load speeds.

Technical challenges included handling high traffic during sales events, implementing complex inventory management across multiple warehouses, and ensuring PCI compliance for payment processing.`,
      solutions: `We built the platform using Next.js for optimal performance and SEO. AI recommendations were implemented using machine learning algorithms that analyze user behavior, purchase history, and style preferences.

Virtual try-on was achieved through WebGL and computer vision APIs. The backend used PostgreSQL for transactional data and Redis for caching and session management. Stripe integration provided secure payment processing.`,
      results: `The platform achieved a 4.2% conversion rate, exceeding targets by 20%. Average order value increased by 35%, and cart abandonment rates decreased by 40%. The site now handles over 100,000 monthly visitors with 99.9% uptime.

The AI recommendation engine drives 45% of all sales, and the virtual try-on feature has a 78% engagement rate among users who interact with it.`,
      completionDate: "2023-12-05",
      projectSize: "Large",
      testimonial: {
        text: "Our sales have tripled since launching the new platform. The AI recommendations are incredibly accurate.",
        author: "Emma Thompson",
        position: "CEO, StyleHub Fashion"
      },
      downloadUrl: "/assets/case-studies/fashion-ecommerce.pdf"
    },
    {
      id: 5,
      title: "Healthcare Management Portal",
      client: "MedCare Systems",
      category: "Web Development",
      industry: "Healthcare",
      technologies: ["Vue.js", "Python Django", "MySQL", "AWS"],
      thumbnail: "https://dxc.scene7.com/is/image/dxc/AdobeStock_692437578?$4x3-600px$",
      images: [
        "https://dxc.scene7.com/is/image/dxc/AdobeStock_692437578?$4x3-600px$",
        "https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=800&h=600&fit=crop"
      ],
      description: "Comprehensive healthcare management system with patient records, appointment scheduling, and telemedicine capabilities.",
      objectives: `Develop a HIPAA-compliant healthcare management system that would streamline patient care processes and improve operational efficiency. The system needed to integrate patient records, appointment scheduling, billing, and telemedicine features.

Key objectives included reducing administrative overhead by 50%, improving patient satisfaction scores, and ensuring full compliance with healthcare regulations.`,
      challenges: `The primary challenge was ensuring HIPAA compliance while maintaining system usability and performance. We needed to implement robust security measures including data encryption, access controls, and comprehensive audit trails.

Integration with existing medical equipment and third-party systems required careful planning and testing. The telemedicine component needed to provide high-quality video calls while maintaining patient privacy.`,
      solutions: `We developed the system using Vue.js for the frontend and Python Django for the backend, ensuring robust security and scalability. All patient data is encrypted both in transit and at rest using AES-256 encryption.

The telemedicine feature was implemented using WebRTC for peer-to-peer video calls with end-to-end encryption. Integration with medical devices was achieved through HL7 FHIR standards for interoperability.`,
      results: `The system reduced administrative tasks by 55% and improved patient satisfaction scores from 3.8 to 4.6 out of 5. Appointment scheduling efficiency increased by 70%, and the telemedicine feature now handles over 500 consultations monthly.

The platform successfully passed all HIPAA compliance audits and has been adopted by 12 healthcare facilities across the region.`,
      completionDate: "2023-10-30",
      projectSize: "Large",
      testimonial: {
        text: "This system has revolutionized our patient care processes. Everything is more efficient and secure.",
        author: "Dr. Jennifer Adams",
        position: "Chief Medical Officer, MedCare Systems"
      },
      downloadUrl: "/assets/case-studies/healthcare-portal.pdf"
    },
    {
      id: 6,
      title: "Real Estate CRM Mobile App",
      client: "PropertyPro Realty",
      category: "Mobile Apps",
      industry: "Real Estate",
      technologies: ["Flutter", "Firebase", "Google Maps API", "Twilio"],
      thumbnail: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop",
      images: [
        "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1582407947304-fd86f028f716?w=800&h=600&fit=crop",
        "https://images.unsplash.com/photo-1516156008625-3a9d6067fab5?w=800&h=600&fit=crop"
      ],
      description: "Mobile CRM solution for real estate agents with lead management, property listings, and client communication tools.",
      objectives: `Create a comprehensive mobile CRM that would help real estate agents manage leads, track property showings, and maintain client relationships on the go. The app needed to integrate with MLS systems and provide offline functionality.

Goals included increasing agent productivity by 40%, improving lead conversion rates, and providing seamless communication tools for client interactions.`,
      challenges: `The main challenge was creating an intuitive interface that could handle complex CRM functionality on mobile devices. We needed to integrate with multiple MLS systems that had different APIs and data formats.

Offline functionality was crucial as agents often work in areas with poor connectivity. The app also needed to handle large amounts of property data and images efficiently.`,
      solutions: `We developed the app using Flutter for cross-platform compatibility. Firebase provided real-time synchronization and offline capabilities through local caching. Integration with MLS systems was achieved through custom API adapters.

Google Maps API integration provided location-based property search and route optimization for showings. Twilio integration enabled SMS and voice communication directly from the app.`,
      results: `Agent productivity increased by 48%, exceeding the target. Lead conversion rates improved by 32%, and the app now manages over $50M in property listings. Agent satisfaction scores reached 4.7/5 stars.

The offline functionality proved essential, with 60% of agents regularly using the app in areas with limited connectivity. Property showing scheduling efficiency improved by 65%.`,
      completionDate: "2023-08-15",
      projectSize: "Medium",
      testimonial: {
        text: "This app has become indispensable for our agents. It\'s transformed how we manage our business.",
        author: "Mark Wilson",
        position: "Broker Owner, PropertyPro Realty"
      },
      downloadUrl: "/assets/case-studies/realestate-crm.pdf"
    }
  ];

  const filteredProjects = projects.filter(project => {
    const matchesCategory = selectedCategory === 'All' || project.category === selectedCategory;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.client.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.technologies.some(tech => tech.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    switch (sortBy) {
      case 'recent':
        return new Date(b.completionDate) - new Date(a.completionDate);
      case 'oldest':
        return new Date(a.completionDate) - new Date(b.completionDate);
      case 'alphabetical':
        return a.title.localeCompare(b.title);
      case 'client':
        return a.client.localeCompare(b.client);
      default:
        return 0;
    }
  });

  const displayedProjects = sortedProjects.slice(0, visibleProjects);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const loadMoreProjects = () => {
    setVisibleProjects(prev => Math.min(prev + 6, sortedProjects.length));
  };

  const SkeletonCard = () => (
    <div className="bg-surface rounded-lg shadow-sm border border-border overflow-hidden animate-pulse">
      <div className="h-48 bg-secondary-200"></div>
      <div className="p-6">
        <div className="h-4 bg-secondary-200 rounded mb-2"></div>
        <div className="h-3 bg-secondary-200 rounded mb-4 w-2/3"></div>
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="h-6 bg-secondary-200 rounded w-16"></div>
          <div className="h-6 bg-secondary-200 rounded w-20"></div>
          <div className="h-6 bg-secondary-200 rounded w-18"></div>
        </div>
        <div className="h-3 bg-secondary-200 rounded mb-2"></div>
        <div className="h-3 bg-secondary-200 rounded w-3/4"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header Section */}
      <div className="bg-surface border-b border-border pt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-heading font-bold text-text-primary mb-6">
                Our Portfolio
              </h1>
              <p className="text-xl text-text-secondary max-w-3xl mx-auto mb-8">
                Discover how we've helped businesses transform their digital presence through innovative solutions and cutting-edge technology.
              </p>
              <div className="flex items-center justify-center space-x-8 text-sm text-text-secondary">
                <div className="flex items-center space-x-2">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <span>24+ Completed Projects</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Users" size={20} className="text-primary" />
                  <span>15+ Happy Clients</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Icon name="Award" size={20} className="text-accent" />
                  <span>98% Success Rate</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Filter and Search Section */}
      <FilterBar
        categories={categories}
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        sortBy={sortBy}
        onSortChange={setSortBy}
        projectCount={filteredProjects.length}
      />

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <AnimatePresence mode="wait">
          {isLoading ? (
            <motion.div
              key="loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {[...Array(9)].map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="projects"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              {displayedProjects.map((project, index) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  index={index}
                  onViewDetails={() => setSelectedProject(project)}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Load More Button */}
        {!isLoading && displayedProjects.length < sortedProjects.length && (
          <div className="text-center mt-12">
            <button
              onClick={loadMoreProjects}
              className="btn-primary px-8 py-3 text-lg"
            >
              Load More Projects
              <Icon name="ChevronDown" size={20} className="ml-2" />
            </button>
          </div>
        )}

        {/* No Results */}
        {!isLoading && filteredProjects.length === 0 && (
          <div className="text-center py-16">
            <div className="w-24 h-24 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="Search" size={48} className="text-secondary-400" />
            </div>
            <h3 className="text-2xl font-semibold text-text-primary mb-4">No Projects Found</h3>
            <p className="text-text-secondary mb-8">
              Try adjusting your search criteria or browse all projects.
            </p>
            <button
              onClick={() => {
                setSelectedCategory('All');
                setSearchQuery('');
              }}
              className="btn-primary"
            >
              View All Projects
            </button>
          </div>
        )}
      </div>

      {/* Client Testimonials */}
      <ClientTestimonials projects={projects} />

      {/* Call to Action */}
      <div className="bg-primary text-white py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
            Ready to Start Your Project?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Let's discuss how we can help transform your business with innovative technology solutions.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-primary hover:bg-gray-50 px-8 py-3 rounded-md font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary">
              Start a Project
            </button>
            <button className="border-2 border-white text-white hover:bg-white hover:text-primary px-8 py-3 rounded-md font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-primary">
              Schedule Consultation
            </button>
          </div>
        </div>
      </div>

      {/* Project Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
      <Footer />
    </div>
  );
};

export default Portfolio;